import { useCallback, useEffect, useState } from 'react';
import { useForm, ControllerRenderProps } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { format, parse } from 'date-fns';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import GoZigLogoLight from '@/assets/logos/goZig-logo-light.svg';
import ZigZag from '@/assets/zigzag.svg';
import { DecorativeSvgMask } from '@/components/DecorativeSvgMask';

import { UserProfileModel } from '@/apiTypes/UserTypes';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DateOfBirthInput } from '@/components/DateOfBirthInput/DateOfBirthInput';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { calculateAge } from '@/helpers/profileHelpers';
import { useUpdateUserProfile } from '@/hooks/user/useUpdateUserProfile';
import { useUser } from '@/hooks/user/useUser';
import { useFrontendConfigs } from '@/hooks/useFrontendConfigs';
import { pushAccountRegistrationDataLayer } from '@/lib/gtmAccountRegistration';
import { useGetAllStatesAndTerritoriesQuery } from '@/services/ApiService';

import { GetStartedFormSchema } from './GetStartedFormSchema';
import { ResponsiveDialog } from '@/components/ResponsiveDialog/ResponsiveDialog';
import { CmsPageBodyBySlug } from '@/components/cms/CmsPageBodyBySlug';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { TermsAndConditions } from '@/pages/TermsAndConditions/TermsAndConditions';
import { PrivacyPolicy } from '@/pages/PrivacyPolicy/PrivacyPolicy';
import { cn, getCookie } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { H1, P } from '@/components/ui/typography';
import { Card } from '@/components/ui/card';

const STATIC_LEGAL_SUBTITLE = 'Last Modified: October 12, 2023';

const authMaterialOutlinedInputClassName = cn(
    'h-13 rounded-[4px] border bg-white pl-[22px] pr-[4px] shadow-none transition-[border-color,box-shadow]',
    'border-[#5c564f] light:border-[#5c564f]',
    'text-base font-normal text-gz-neutral-800 light:text-gz-neutral-800',
    'placeholder:text-[#5c564f] light:placeholder:text-[#5c564f]',
    'focus-visible:border-gz-marine-700',
    'aria-invalid:border-destructive',
);

const authDobFieldShellClassName = cn(
    'group relative rounded-[4px] border bg-white pl-[22px] pr-[4px] transition-[border-color,box-shadow]',
    'border-[#5c564f] light:border-[#5c564f]',
    'focus-within:border-gz-marine-700',
    'has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2',
    'has-[input:focus-visible]:outline-solid has-[input:focus-visible]:outline-focus-ring',
    'has-[input[aria-invalid=true]]:border-destructive',
);

const authDobInnerInputClassName = cn(
    'h-13 w-full border-0 bg-transparent px-0 py-0 shadow-none outline-none',
    'focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none',
    'text-base font-normal text-gz-neutral-800',
    'placeholder:text-transparent focus:placeholder:text-[#5c564f] light:focus:placeholder:text-[#5c564f]',
    'pr-10',
);

const authOutlinedSelectShellClassName = cn(
    'group relative rounded-[4px] border bg-white pl-[22px] pr-3 py-0 transition-[border-color,box-shadow]',
    'border-[#5c564f] light:border-[#5c564f]',
    'focus-within:border-gz-marine-700',
    'has-[button:focus-visible]:outline-2 has-[button:focus-visible]:outline-offset-2',
    'has-[button:focus-visible]:outline-solid has-[button:focus-visible]:outline-focus-ring',
    'has-[button[aria-invalid=true]]:border-destructive',
);

const muiStackedFieldsClassName = 'flex flex-col gap-4';

const authFloatingLabelClassName = cn(
    'pointer-events-none absolute left-[18px] top-0 z-10 -translate-y-1/2 bg-white px-1',
    'text-xs font-normal leading-none text-[#5c564f] light:text-[#5c564f] transition-all duration-150',
);

const authFloatingInputLabelClassName = cn(
    authFloatingLabelClassName,
    'group-[:has(input:placeholder-shown):not(:focus-within)]:top-1/2',
    'group-[:has(input:placeholder-shown):not(:focus-within)]:text-base',
);

const authFloatingSelectLabelClassName = cn(
    authFloatingLabelClassName,
    'group-[:has(button[data-placeholder])]:top-1/2',
    'group-[:has(button[data-placeholder])]:text-base',
);

const authSelectTriggerClassName = cn(
    'flex h-12 w-full items-center justify-between gap-2 rounded-none border-0 bg-transparent px-0 py-0',
    'text-base font-normal text-gz-neutral-800 shadow-none outline-none',
    'placeholder:text-[#5c564f] light:placeholder:text-[#5c564f]',
    'focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:border-0 focus-visible:outline-none',
    '[&>svg]:shrink-0',
);

const legalLinkClassName = 'p-0 !h-auto font-normal text-primary hover:text-primary-dark';

export const GetStartedPage = () => {
    const [termsAndConditionsDialogOpen, setTermsAndConditionsDialogOpen] = useState<boolean>(false);
    const [privacyPolicyDialogOpen, setPrivacyPolicyDialogOpen] = useState<boolean>(false);
    const [isUpdatingProfile, setIsUpdatingProfile] = useState<boolean>(false);

    const { user } = useUser();
    const { featureFlags } = useFeatureFlags();
    const useCmsLegalPages = featureFlags.FEATURE_FLAG_CMS_PUBLIC_PAGES === true;
    const { configs } = useFrontendConfigs();
    const userProfile = user?.userProfile as UserProfileModel;
    const navigate = useNavigate();
    const updateUserProfile = useUpdateUserProfile();
    const { data: allStates = [] } = useGetAllStatesAndTerritoriesQuery();

    const [age, setAge] = useState(0);
    const [underAgeDialogOpen, setUnderAgeDialogOpen] = useState(false);

    useEffect(() => {
        setAge(userProfile?.dateOfBirth ? calculateAge(parse(userProfile.dateOfBirth.split('T')[0], 'yyyy-MM-dd', new Date())) : 0);
    }, [userProfile?.dateOfBirth]);

    const form = useForm<z.infer<typeof GetStartedFormSchema>>({
        resolver: zodResolver(GetStartedFormSchema),
        values: {
            firstName: userProfile?.firstName ?? '',
            dateOfBirth: userProfile?.dateOfBirth ? parse(userProfile.dateOfBirth.split('T')[0], 'yyyy-MM-dd', new Date()) : null,
            currentUsStateOrTerritoryId: userProfile?.currentUsStateOrTerritoryId ?? '',
            agreedToLegal: Boolean(userProfile?.agreedToTerms && userProfile?.agreedToPrivacyPolicy),
            agreedToMarketingUse: userProfile?.agreedToMarketingUse ?? false,
        },
    });

    const onSubmit = useCallback(async (data: z.infer<typeof GetStartedFormSchema>) => {
        const profilePayload = {
            firstName: data.firstName,
            dateOfBirth: data.dateOfBirth ? format(data.dateOfBirth, 'yyyy-MM-dd') : undefined,
            birthYear: data.dateOfBirth?.getFullYear(),
            currentUsStateOrTerritoryId: data.currentUsStateOrTerritoryId,
            agreedToTerms: data.agreedToLegal,
            agreedToPrivacyPolicy: data.agreedToLegal,
            agreedToMarketingUse: age >= 13 ? (data.agreedToMarketingUse ?? false) : false,
        };

        if (age < 13) {
            setUnderAgeDialogOpen(true);

            if (userProfile?.dateOfBirth !== data.dateOfBirth?.toLocaleDateString()) {
                setIsUpdatingProfile(true);
                try {
                    await updateUserProfile({
                        ...profilePayload,
                        agreedToMarketingUse: false,
                        restrictedDueToAge: true,
                    });
                }
                finally {
                    setIsUpdatingProfile(false);
                }
            }

            return;
        }

        setIsUpdatingProfile(true);
        try {
            const { error } = await updateUserProfile({
                ...profilePayload,
                restrictedDueToAge: false,
            });
            if (error) {
                toast.error('We couldn\'t save your details. Please try again.');
                setIsUpdatingProfile(false);
                return;
            }
            if (configs['googleTagManagerId'] && user?.id) {
                pushAccountRegistrationDataLayer({
                    user_id: user.id,
                    ...(user.uid ? { uid: user.uid } : {}),
                });
            }
            const cpage = getCookie('cpage');
            const isAuthOnlyPage = cpage === '/login-error' || cpage?.startsWith('/login-error?')
                || cpage === '/change-password-migration' || cpage?.startsWith('/change-password-migration?')
                || cpage === '/check-your-email' || cpage?.startsWith('/check-your-email?');
            if (cpage && cpage !== '/get-started' && !isAuthOnlyPage) {
                navigate(cpage);
            }
            else {
                navigate('/dashboard');
            }
        }
        catch {
            setIsUpdatingProfile(false);
        }
    }, [age, configs, navigate, updateUserProfile, user?.id, user?.uid, userProfile?.dateOfBirth]);

    const onToggleTermsAndConditions = useCallback(() => {
        setTermsAndConditionsDialogOpen(!termsAndConditionsDialogOpen);
    }, [termsAndConditionsDialogOpen]);

    const onTogglePrivacyPolicy = useCallback(() => {
        setPrivacyPolicyDialogOpen(!privacyPolicyDialogOpen);
    }, [privacyPolicyDialogOpen]);

    const renderTextFormItem = ({
        field,
        label,
    }: {
        field: ControllerRenderProps<z.infer<typeof GetStartedFormSchema>, 'firstName'>;
        label: string;
    }) => {
        return (
            <FormItem className="mb-0 gap-0">
                <div className="group relative">
                    <FormControl>
                        <Input
                            {...field}
                            aria-required={true}
                            autoComplete="given-name"
                            // Blank placeholder keeps :placeholder-shown driving the label without showing text under it.
                            placeholder=" "
                            className={cn(authMaterialOutlinedInputClassName, 'rr-block')}
                            disableFlex={true}
                            type="text"
                            data-heap-redact-attributes="value"
                        />
                    </FormControl>
                    <FormLabel className={authFloatingInputLabelClassName}>{label}</FormLabel>
                </div>
                <FormMessage className="mt-1.5" />
            </FormItem>
        );
    };

    if (isUpdatingProfile) {
        return (
            <div className={cn('min-h-screen w-full flex items-center justify-center', 'light:bg-background dark:bg-background', 'light')}>
                <span className="material-symbols-rounded !text-7xl animate-spin text-white light:text-background dark:text-foreground">sync</span>
            </div>
        );
    }

    return (
        <div className={cn('relative min-h-screen flex items-center justify-center p-6', 'light')}>
            <DecorativeSvgMask
                src={ZigZag}
                maskFillClassName="light:bg-gz-accent-orange dark:bg-gz-marine-500"
                opacityClassName="opacity-15"
                maskSize="160%"
                maskPosition="center"
            />
            <Card gap={0} shadow="lg" className="!rounded-2xl !px-8 lg:!px-10 !pt-8 lg:!pt-10 !pb-6 lg:!pb-8 w-full max-w-[440px] bg-white relative border-0">
                <div className="flex items-center justify-center">
                    <img
                        className="cursor-pointer w-32 lg:w-40 h-auto"
                        src={GoZigLogoLight}
                        alt="GoZig logo"
                    />
                </div>

                <H1
                    variant="type-body-default"
                    className={cn(
                        'mt-6 mb-4 text-center',
                        'font-[ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,Segoe_UI,Roboto,Helvetica,sans-serif]',
                        '!text-[24px] font-normal leading-9 tracking-normal',
                        'text-gz-neutral-800 light:text-gz-neutral-800',
                    )}
                >
                    Account set up
                </H1>
                <P variant="type-body-default" className="mb-6 text-center !text-[20px] leading-7 text-gz-neutral-800 light:text-gz-neutral-800">
                    Tell us a bit about yourself.
                </P>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col space-y-6">
                        <div className={muiStackedFieldsClassName}>
                            <FormField
                                control={form.control}
                                name="firstName"
                                render={({ field }) => renderTextFormItem({ field, label: 'First name *' })}
                            />
                            <FormField
                                control={form.control}
                                name="dateOfBirth"
                                render={({ field }) => (
                                    <FormItem className="flex flex-col gap-0">
                                        <div className={cn(authDobFieldShellClassName)}>
                                            <FormControl>
                                                <DateOfBirthInput
                                                    value={field.value}
                                                    onChange={(date) => {
                                                        form.setValue('dateOfBirth', date);
                                                        if (date) {
                                                            setAge(calculateAge(date));
                                                            form.trigger('dateOfBirth');
                                                        }
                                                    }}
                                                    onError={message => form.setError('dateOfBirth', { message })}
                                                    onClearError={() => form.clearErrors('dateOfBirth')}
                                                    aria-required={true}
                                                    autoComplete="bday"
                                                    placeholder="MM/DD/YYYY"
                                                    inputClassName={cn(authDobInnerInputClassName, 'rr-block')}
                                                    buttonClassName="h-9 w-11 text-gz-neutral-500 hover:bg-transparent hover:text-gz-marine-700"
                                                />
                                            </FormControl>
                                            <FormLabel className={authFloatingInputLabelClassName}>Date of birth *</FormLabel>
                                        </div>
                                        <FormMessage className="mt-1.5" />
                                    </FormItem>
                                )}
                            />
                            <div data-testid="current-us-state-or-territory-id">
                                <FormField
                                    control={form.control}
                                    name="currentUsStateOrTerritoryId"
                                    render={({ field }) => (
                                        <FormItem className="mb-0 gap-0">
                                            <div className={authOutlinedSelectShellClassName}>
                                                <Select value={field.value} onValueChange={field.onChange}>
                                                    <FormControl>
                                                        <SelectTrigger aria-required={true} className={cn(authSelectTriggerClassName, 'w-full [&:not([data-placeholder])]:text-gz-neutral-800 data-[placeholder]:text-[#5c564f]')}>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent className="max-h-64 overflow-y-auto light bg-white text-foreground">
                                                        {allStates.map(option => (
                                                            <SelectItem key={option.id} value={option.id} aria-label={`${option.name} (${option.abbreviation})`}>
                                                                {`${option.name} (${option.abbreviation})`}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormLabel className={authFloatingSelectLabelClassName}>State *</FormLabel>
                                            </div>
                                            <FormMessage className="mt-1.5" />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>

                        <div className="w-full flex">
                            <Button
                                variant="primary"
                                size="large"
                                className="w-full font-medium shadow-none"
                                disabled={!form.formState.isValid || form.formState.isSubmitting}
                                type="submit"
                                data-track-click="Account Registration Submitted"
                                data-track-location="account-setup"
                                data-track-button-name="registration"
                            >
                                Get started!
                            </Button>
                        </div>

                        <div className={muiStackedFieldsClassName} role="group" aria-label="Agreements">
                            <FormField
                                control={form.control}
                                name="agreedToLegal"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-start gap-2 space-y-0">
                                        <FormControl>
                                            <Checkbox
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                                aria-required={true}
                                                className="mt-0.5"
                                            />
                                        </FormControl>
                                        <FormLabel className="text-sm font-normal leading-snug text-gz-neutral-700 light:text-gz-neutral-700">
                                            I agree to the
                                            {' '}
                                            <Button
                                                type="button"
                                                variant="link"
                                                size="sm"
                                                className={legalLinkClassName}
                                                onClick={onToggleTermsAndConditions}
                                            >
                                                Terms of use
                                            </Button>
                                            {' '}
                                            and
                                            {' '}
                                            <Button
                                                type="button"
                                                variant="link"
                                                size="sm"
                                                className={legalLinkClassName}
                                                onClick={onTogglePrivacyPolicy}
                                            >
                                                Privacy policy
                                            </Button>
                                            <span className="text-destructive"> *</span>
                                        </FormLabel>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="agreedToMarketingUse"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-start gap-2 space-y-0">
                                        <FormControl>
                                            <Checkbox
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                                className="mt-0.5"
                                            />
                                        </FormControl>
                                        <FormLabel className="text-sm font-normal leading-snug text-gz-neutral-700 light:text-gz-neutral-700">
                                            I agree to receive product and marketing communications. Opt out at any time.
                                        </FormLabel>
                                    </FormItem>
                                )}
                            />
                        </div>

                        <ResponsiveDialog
                            title="Terms of Use"
                            subtitle={useCmsLegalPages ? undefined : STATIC_LEGAL_SUBTITLE}
                            isOpen={termsAndConditionsDialogOpen}
                            onClose={onToggleTermsAndConditions}
                            footer={<Button className="w-full" onClick={onToggleTermsAndConditions}>Close</Button>}
                        >
                            {useCmsLegalPages
                                ? <CmsPageBodyBySlug slug="terms" />
                                : <TermsAndConditions showTitle={false} showLastModified={false} />}
                        </ResponsiveDialog>
                        <ResponsiveDialog
                            title="Privacy Policy"
                            subtitle={useCmsLegalPages ? undefined : STATIC_LEGAL_SUBTITLE}
                            isOpen={privacyPolicyDialogOpen}
                            onClose={onTogglePrivacyPolicy}
                            footer={<Button className="w-full" onClick={onTogglePrivacyPolicy}>Close</Button>}
                        >
                            {useCmsLegalPages
                                ? <CmsPageBodyBySlug slug="privacy" />
                                : <PrivacyPolicy showTitle={false} showLastModified={false} />}
                        </ResponsiveDialog>
                        <Dialog
                            open={underAgeDialogOpen}
                            onOpenChange={setUnderAgeDialogOpen}
                        >
                            <DialogContent className="border-none bg-secondary-dark light:bg-background dark:bg-background rounded-lg text-white light:text-foreground dark:text-foreground">
                                <DialogHeader>
                                    <DialogTitle className="text-left text-xl light:text-foreground dark:text-foreground">
                                        You need to be 13 or older to
                                        continue.
                                    </DialogTitle>
                                </DialogHeader>
                                <DialogDescription className="text-white light:text-foreground dark:text-foreground">
                                    You need to be 13 years or older to use [Career Explorer]. We encourage you to access
                                    ASA's other platform
                                    {' '}
                                    <a href="https://futurescape.asa.org" className="underline">futurescape.asa.org</a>
                                </DialogDescription>
                            </DialogContent>
                        </Dialog>
                    </form>
                </Form>
            </Card>
        </div>
    );
};
